/**
 * BillingSuccessPage — landing dopo redirect Stripe Checkout.
 *
 * URL: /billing/success?session_id={CHECKOUT_SESSION_ID}
 *
 * Flow:
 *  1. Estrae session_id dalla query string.
 *  2. Polla GET /api/billing/checkout-status/{session_id} ogni 2s (max 12 tries).
 *  3. Quando payment_status === "paid" OR status === "complete" → success view.
 *  4. Refresha l'AuthContext (refreshMe) per propagare il nuovo is_premium.
 *
 * Note: il webhook backend (più affidabile) farà l'aggiornamento canonico
 * dell'utente; il polling qui serve solo per UX (mostra "completato" appena
 * possibile invece di rimanere su loader infinito).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  bg: "#050507",
  surface: "#0A0A0D",
  border: "rgba(255,255,255,0.08)",
  text: "#FFFFFF",
  muted: "#71717A",
  accent: "#C8FF00",
  green: "#22FFA3",
  red: "#EF4444",
};

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { getToken, refreshMe } = useAuth();
  const sessionId = params.get("session_id");

  const [state, setState] = useState("polling"); // polling | paid | failed | expired | timeout
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    // ── Branch A: We have a Stripe Checkout session_id → poll its status.
    //    This is the API-created Checkout Session path (Lifetime, and any
    //    plan that doesn't use a Payment Link).
    if (sessionId) {
      const poll = async () => {
        if (cancelled) return;
        attemptsRef.current += 1;
        try {
          const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (cancelled) return;
          setDetails(res.data);
          if (res.data.payment_status === "paid" || res.data.status === "complete") {
            setState("paid");
            // Refresh user JWT-context so navbar instantly shows Premium.
            refreshMe?.();
            return;
          }
          if (res.data.status === "expired") {
            setState("expired");
            return;
          }
        } catch {
          // network glitch — retry until max attempts
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      };
      poll();
      return () => { cancelled = true; };
    }

    // ── Branch B: No session_id (Payment Link flow). We trigger a server-
    //    side self-sync against Stripe and poll the user's premium flag
    //    until is_premium=true. Same end-result, different mechanism.
    const pollByRefresh = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        // Ask backend to pull the latest subscription from Stripe for THIS
        // user (uses email). This bypasses the webhook delay for Payment
        // Links and activates Premium immediately.
        await axios.post(`${API}/billing/sync-self`, {}, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }).catch(() => null);
        const me = await refreshMe?.();
        if (me?.is_premium) {
          setState("paid");
          return;
        }
      } catch { /* retry */ }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(pollByRefresh, POLL_INTERVAL_MS);
    };
    pollByRefresh();
    return () => { cancelled = true; };
  }, [sessionId, getToken, refreshMe]);

  const renderBody = () => {
    if (state === "polling") return (
      <>
        <Loader2 size={42} className="animate-spin" style={{ color: C.accent }} />
        <h1 data-testid="billing-success-loading" style={{
          marginTop: 24, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "1.8rem", letterSpacing: "0.06em", textTransform: "uppercase",
        }}>Verifica pagamento in corso…</h1>
        <p style={{ color: C.muted, marginTop: 10, fontSize: "0.85rem" }}>
          Stripe sta confermando la transazione. Non chiudere questa pagina.
        </p>
      </>
    );
    if (state === "paid") return (
      <>
        <CheckCircle2 size={56} style={{ color: C.green, filter: `drop-shadow(0 0 14px ${C.green}80)` }} />
        <h1 data-testid="billing-success-paid" style={{
          marginTop: 22, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "2.2rem", letterSpacing: "0.06em", textTransform: "uppercase",
          textShadow: `0 0 24px ${C.green}66`,
        }}>Benvenuto in <span style={{ color: C.accent }}>BetCracker Premium</span>!</h1>
        <p style={{ color: "#D4D4D8", marginTop: 14, fontSize: "1rem", maxWidth: 520, lineHeight: 1.55 }}>
          Goditi tutte le funzioni del software: Brain Lab, Samurai illimitato,
          Target Kitano, Hyper Combo, Daymio Trends, Monte Carlo e molto altro.
        </p>
        {details && details.amount_total > 0 && (
          <div style={{
            marginTop: 18, padding: "0.6rem 1rem",
            background: "rgba(34,255,163,0.05)", border: `1px solid ${C.green}44`,
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.74rem", color: C.green,
          }}>
            Importo addebitato: € {(details.amount_total / 100).toFixed(2)} {details.currency}
          </div>
        )}
        <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={() => navigate("/")}
            data-testid="billing-success-go-home"
            style={btnPrimary()}>
            <Sparkles size={14} /> Vai alla Home
          </button>
          <button
            onClick={() => navigate("/account")}
            data-testid="billing-success-go-account"
            style={btnSecondary()}>
            Vedi Account <ArrowRight size={13} />
          </button>
        </div>
      </>
    );
    if (state === "expired" || state === "failed") return (
      <>
        <XCircle size={42} style={{ color: C.red }} />
        <h1 style={{
          marginTop: 22, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "1.8rem", letterSpacing: "0.06em", textTransform: "uppercase",
        }}>
          {state === "expired" ? "Sessione scaduta" : "Sessione non valida"}
        </h1>
        <p style={{ color: C.muted, marginTop: 10, fontSize: "0.9rem" }}>
          Non è stato addebitato nulla. Puoi ritentare quando vuoi.
        </p>
        <button
          onClick={() => navigate("/pricing")}
          data-testid="billing-success-retry"
          style={{ ...btnPrimary(), marginTop: 22 }}>
          Torna ai piani
        </button>
      </>
    );
    // timeout
    return (
      <>
        <Loader2 size={42} style={{ color: C.accent }} />
        <h1 style={{
          marginTop: 22, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
          fontSize: "1.6rem", letterSpacing: "0.06em", textTransform: "uppercase",
        }}>Conferma in attesa…</h1>
        <p style={{ color: C.muted, marginTop: 10, fontSize: "0.88rem", maxWidth: 480 }}>
          Stripe potrebbe impiegare ancora qualche minuto per confermare.
          Riceverai una email e la pagina Account si aggiornerà automaticamente.
        </p>
        <button
          onClick={() => navigate("/account")}
          data-testid="billing-success-timeout-account"
          style={{ ...btnPrimary(), marginTop: 22 }}>
          Vai all'Account
        </button>
      </>
    );
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text }} className="grid-bg">
      <main style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "2rem 1.5rem", textAlign: "center",
      }}>
        {renderBody()}
      </main>
    </div>
  );
}

function btnPrimary() {
  return {
    padding: "0.75rem 1.3rem", background: C.accent, color: "#020202",
    border: "none", fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
    fontSize: "0.92rem", cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
    boxShadow: `0 0 22px ${C.accent}44`,
  };
}
function btnSecondary() {
  return {
    padding: "0.7rem 1.2rem", background: "transparent", color: C.text,
    border: `1px solid ${C.border}`,
    fontFamily: "'Barlow Condensed',sans-serif",
    fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
    fontSize: "0.88rem", cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
  };
}
